/**
	* Popup box view
	* Handles: popup boxes
	* @module PopupBox
*/
import { useState, useEffect } from "react";

import Vector2 from "lib/classes/vector2";
import { Popup, PopupProps } from "lib/classes/popup";
import { time } from "lib/utilities/misc";

import $ from "jquery";

import "styles/css/Popup.scss";

interface PopupBoxProps {
	popup:Popup,
	index:number,
	onClose:Function
}
 
 /**
 	* PopupBox component
	* 
	* @param PopupBoxProps.popup	-	Popup instance
	* @param PopupBoxProps.onClose	-	Close method passed by any view
	* 
	* @remarks
	* Popups are currently drawn on each view individually. An onClose
	* method passed to this component is dependent on the state of the
	* view that is drawing the popup.
 */
function PopupBox({popup, index, onClose}:PopupBoxProps) {
	const [indexOffset, setIndexOffset] = useState<number>(_ui.getTopPopupIndex()+1);
	const [clickedUp, setClickedUp] = useState<number>(0);
	const [isDragging, setDragging] = useState<boolean>(false);
	const [dragOffset, setDragOffset] = useState<Vector2>(new Vector2());
	const [initialOffset, setInitialOffset] = useState<Vector2>(new Vector2());
	const [popupPosition, setPopupPosition] = useState<Vector2>(new Vector2());
	const [showing, setShowing] = useState<boolean>(false);
		
	const popupContainerId:string = `popup-container-${popup.id}`;
	const popupWindowId:string = `popup-window-${popup.id}`;
	const popupContentId:string = `popup-content-${popup.id}`;
	
	useEffect(() => {
		setTimeout(showPopup, 50);
	}, [popup.showing]);
	
	useEffect(() => {
		_ui.tooltip.rebuild();
	}, [showing]);
	
	useEffect(() => {
		window.addEventListener("mousemove", onPopupDrag);
		window.addEventListener("touchmove", onPopupDrag);
		
		window.addEventListener("mouseup", onMouseUp);
		window.addEventListener("touchend", onMouseUp);
		
		setInitialOffset(new Vector2(popupPosition.x, popupPosition.y));
		
		return () => {
			window.removeEventListener("mousemove", onPopupDrag);
			window.removeEventListener("touchmove", onPopupDrag);
			
			window.removeEventListener("mouseup", onMouseUp);
			window.removeEventListener("touchend", onMouseUp);
		};
	}, [isDragging]);
	
	useEffect(() => {
		transform(popupPosition);
	}, [popupPosition]);
	
	const closePopup = (e:any) => {
		if(isDragging) return;
		
		if(time() - clickedUp < 0.01 && clickedUp > 0) {
			setClickedUp(0);
			return;
		}
		
		if(popup.closeable)
			onClose();
	}
	const drawCloseButton = () => {
		if(!popup.closeable) return;
		
		return (
			<div style={{float: "right", zIndex: 10}}>
				<button onClick={closePopup}>X</button>
			</div>
		)
	}
	const drawTitleBar = () => {
		const content:any = popup.getTitle.bind(popup)();
		
		if(!content) return;
		
		return (
			<div className="popup__titlebar">
				{content}
				
				{drawCloseButton()}
				
				<div className="popup__barclick" onMouseDown={onTitleBarMouseDown}></div>
			</div>
		)
	}
	const drawContent = () => {
		return (
			<div id={popupContentId} className="popup__window__content" style={popup.styleContainer}>
				{popup.getContent.bind(popup)()}
			</div>
		)
	}
	const showPopup = () => {
		// Popup isn't literally showing yet but is meant to show
		if(!showing && popup.showing) {
			resetPosition();
		}
		
		setShowing(popup.showing);
	}
	
	/*
		Popup movement handlers
	*/
	const onTitleBarMouseDown = (e:any) => {
		if(isDragging) return;
		
		e.preventDefault();
		
		setDragging(true);
		setIndexOffset(_ui.getTopPopupIndex()+1);
		setDragOffset(new Vector2(e.clientX, e.clientY));
	}
	const onPopupDrag = (e:any) => {
		if(!isDragging) return;
		
		e.preventDefault();
		setPopupPosition(new Vector2(
			e.clientX - dragOffset.x + initialOffset.x,
			e.clientY - dragOffset.y + initialOffset.y
		));
	}
	const onMouseUp = (e:any) => {
		if(!isDragging) return;
		setDragging(false);
		setClickedUp(time());
	}
	const transform = (pos:Vector2) => {
		const popupWindow = $(`#${popupWindowId}`);
		
		popupWindow.css({
			transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`
		});
	}
	const resetPosition = () => {
		transform(popup.defaultPosition || new Vector2());
		setInitialOffset(popup.defaultPosition || new Vector2());
	}
	
	let classContainer:string = `popup${popup.distinct ? " popup--distinct" : ""}`;
	let classWindow:string = "popup__window";
	
	if(showing) {
		classContainer += " popup--show";
		classWindow += " popup__window--show";
	}
	
	const zIndex:number = index + 10 + indexOffset;
	
	_ui.registerPopupIndex(zIndex);
	
	return (
		<div id={popupContainerId} className={classContainer} style={{zIndex: zIndex}}>
			<div id={popupWindowId} className={classWindow} style={popup.style} onClick={(e) => e.stopPropagation()}>
				{drawTitleBar()}
				{drawContent()}
			</div>
		</div>
	)
}

export default PopupBox;